import { navigate } from "gatsby"
import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js"
import React, { useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { isValidPhoneNumber } from "libphonenumber-js"

import Button from "legacy/components/shared/Button"
import { Element, Text } from "legacy/components/shared/base"
import Input from "legacy/components/shared/Input"

export type HeroFormProps = {
  buttonVariation?: string
  formConfirmationText: string
  formErrorText: string
  formHiddenFields?: { [key: string]: string }
  formInputType: string
  formName: string
  formSubmitButtonText: string
  formSuccessDestination?: string
  fullWidthInput: boolean
  inputPlaceholder?: string
}

interface FormValues {
  formInputType: string
  "form-name": string
}

const formattedDefault = (phoneNumber: string): string => {
  const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber)
  return parsedPhoneNumber?.formatNational() ?? ""
}

export const HeroForm: React.FC<HeroFormProps> = ({
  buttonVariation,
  formHiddenFields,
  formName,
  formConfirmationText,
  formErrorText,
  formInputType,
  formSubmitButtonText,
  formSuccessDestination,
  fullWidthInput,
  inputPlaceholder,
}): React.ReactElement => {
  const [formState, setFormState] = useState({
    isSubmitted: false,
  })
  const [hasErrorState, setErrorState] = useState(false)
  const [value, setValue] = useState(formattedDefault(""))

  const form = useForm<FormValues>()
  const watchedInput = form.watch(formInputType, false)

  const encode = (data: FormValues): string => {
    return Object.entries(data)
      .map(
        ([key, value]) =>
          encodeURIComponent(key) + "=" + encodeURIComponent(value),
      )
      .join("&")
  }

  const validatePhone = (phoneNumberValue: string): boolean => {
    if (isValidPhoneNumber(phoneNumberValue, "US")) {
      return true
    } else {
      return false
    }
  }

  const updateNumber = (phoneNumber: string): void => {
    setValue(phoneNumber)
  }

  const onPhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const newValue = e.currentTarget.value
    const asYouType = new AsYouType("US").input(newValue)

    if (newValue.length < value.length) {
      updateNumber(newValue)
    } else {
      updateNumber(asYouType)
    }
  }

  const onSubmit = (data: FormValues): void => {
    if (formInputType === "phone") {
      const isValidPhone = validatePhone(watchedInput)
      if (isValidPhone) {
        setErrorState(false)
      } else {
        setErrorState(true)
        return
      }
    }
    let formAdditionalRequestBody = ""
    if (formHiddenFields) {
      formAdditionalRequestBody =
        formHiddenFields && `&${encode(formHiddenFields)}`
    }

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: `form-name=${formName}&${formInputType}=${encodeURIComponent(data[formInputType])}${encodeURIComponent(formAdditionalRequestBody)}`,
    })
      .then(() => {
        setFormState({
          isSubmitted: true,
        })

        analytics.track("Form CTA Clicked", data)
        if(formSuccessDestination) {
          navigate("/donor-assessment/", { state: data })
        }
      })
      .catch(error => {
        console.error("Error - Unable to submit header form: ", error)
        // Rerender form inputs on error so user can try to resubmit again
        setFormState({
          isSubmitted: false,
        })
      })
  }

  let inputType = "text"

  const isformInputType = formInputType === "phone";

  if (isformInputType) {
    inputType = "tel"
  } else if (formInputType === "email") {
    inputType = "email"
  }

  return (
    <FormProvider {...form}>
      <Element
        as="form"
        data-netlify="true"
        method="POST"
        name={formName}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        {!formState.isSubmitted && (
          <>
            <Element mb="sm" textAlign="center">
              <Input
                border="1SolidOffBlack"
                borderRadius="32px"
                color="offBlack"
                fontSize="16"
                fontFamily="martel"
                fontWeight="600"
                letterSpacing="wide"
                lineHeight="53px"
                maxWidth="100%"
                width={fullWidthInput ? "100%" : "491px"}
                placeholder={inputPlaceholder || formInputType}
                px="sm"
                pt="12"
                pb="10"
                required
                textTransform="uppercase"
                type={inputType}
                variant="block"
                {...(value && { value })}
                {...form.register(formInputType, {
                  formErrorText,
                  onChange: isformInputType && (v => onPhoneNumberChange(v)),
                })}
              />
            </Element>
            {formHiddenFields &&
              Object.entries(formHiddenFields).map(([key, value]) => (
                <input key={key} name={key} type="hidden" value={value} />
              ))}
            <Element textAlign="center">
              <Button
                display="inline-block"
                fontSize={["16", "text.button"]}
                maxWidth="100%"
                minWidth={["auto", "266px"]}
                px="15"
                py="20"
                type="submit"
                variant={buttonVariation || "primarySky"}
                width={fullWidthInput ? "100%" : "auto"}
              >
                {formSubmitButtonText}
              </Button>
            </Element>
            <Input
              type="hidden"
              value={formName}
              {...form.register("form-name")}
            />
          </>
        )}
        {hasErrorState && (
          <Text
            fontSize={["16", "text.error"]}
            m={["20px auto 0"]}
            textAlign="center"
            variant="error"
          >
            {formErrorText}
          </Text>
        )}
        {formState.isSubmitted && (
          <Text textAlign="center">{formConfirmationText}</Text>
        )}
      </Element>
    </FormProvider>
  )
}
